import { useMemo, useState } from "react"
import { Flex, Text, Tab, Group, Skeleton } from "components/common"
import { Progress } from "antd"
import styles from "./index.module.less"
import { t } from "i18next"
import { useGetClientWhatsappOverallScoresQuery } from "features/clients/detail/api"

const progressColorsForScore = (score, total) => {
  const percent = total ? Math.round((score / total) * 100) : score
  if (percent >= 75) return { stroke: "#99d7b4", trail: "#ddf3e5" }
  if (percent >= 50) return { stroke: "#f3e8bd", trail: "#faf3e0" }
  return { stroke: "#e83b39", trail: "#fef3f3" }
}

const engagementBreakdownInsightsSkeleton = (
  <Flex vertical style={{ gap: 8 }}>
    {[1, 2, 3].map(slot => (
      <Skeleton key={slot} type="button" width={400} height={30} active />
    ))}
  </Flex>
)
const EngagementBreakdownInsights = ({ clientId }) => {
  const [selectedBreakdownTheme, setSelectedBreakdownTheme] = useState("overall")

  const { data: overallScoresDataset, isFetching: fetchingOverallScores } = useGetClientWhatsappOverallScoresQuery(
    { clientId },
    { skip: !clientId },
  )

  const progressRowsForTab = useMemo(() => {
    const breakdownByTheme = overallScoresDataset?.scoresBreakdown ?? {}
    const themeTotals = overallScoresDataset?.overallThemeTotals ?? []

    if (selectedBreakdownTheme === "overall") {
      return themeTotals?.map(item => ({
        label: item?.label,
        score: item?.score,
        total: item?.total || 100,
      }))
    }

    const themeScores = breakdownByTheme?.[selectedBreakdownTheme]
    if (!themeScores?.scores) return []
    return themeScores?.scores?.map(entry => ({
      label: entry?.label,
      score: entry?.score,
      total: entry?.total,
    }))
  }, [selectedBreakdownTheme, overallScoresDataset])

  const breakdownTabsForUi = useMemo(() => {
    const breakdownByTheme = overallScoresDataset?.scoresBreakdown ?? {}
    const tabs = [{ tabKey: "overall", tabTitle: <Text className="fs14 gray-800">{t("Overall")}</Text> }]
    Object.keys(breakdownByTheme).forEach(themeName => {
      tabs.push({
        tabKey: themeName,
        tabTitle: <Text className="fs14 gray-800">{themeName}</Text>,
      })
    })
    return tabs
  }, [overallScoresDataset])

  if (fetchingOverallScores) {
    return engagementBreakdownInsightsSkeleton
  }

  return (
    <Tab
      activeKey={selectedBreakdownTheme}
      onTabClick={key => setSelectedBreakdownTheme(key)}
      items={breakdownTabsForUi?.map(tab => ({
        key: tab?.tabKey,
        label: tab?.tabTitle,
        children: (
          <Group className={styles.progressGrid} template="repeat(2, 1fr)" gap="20px">
            {progressRowsForTab.length > 0 ? (
              progressRowsForTab?.map((row, idx) => {
                const { stroke, trail } = progressColorsForScore(row?.score, row?.total)
                return (
                  <Flex vertical key={idx} gap="6px" className={styles.progressCard}>
                    <Flex justify="space-between" align="center">
                      <Text className="fs14 gray-800">{row?.label}</Text>
                      <Text className="fs14">
                        {row?.score}/{row?.total || 100}
                      </Text>
                    </Flex>
                    <Progress
                      percent={row?.total ? Math.round((row?.score / row?.total) * 100) : row?.score}
                      showInfo={false}
                      strokeColor={stroke}
                      trailColor={trail}
                      strokeWidth={18}
                    />
                  </Flex>
                )
              })
            ) : (
              <Text type="secondary">{t("No breakdown data available")}</Text>
            )}
          </Group>
        ),
      }))}
    />
  )
}

export default EngagementBreakdownInsights
